import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calculator, RotateCcw, Download } from 'lucide-react';
import axios from 'axios';

const BillSummary = ({ 
  items, 
  attendees, 
  tipPercentage, 
  onTipChange, 
  onCalculate, 
  calculations, 
  onBack, 
  onReset 
}) => {
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (items.length > 0 && attendees.length > 0) {
      calculateBill();
    }
  }, [items, attendees, tipPercentage]); // eslint-disable-line react-hooks/exhaustive-deps


  const calculateBill = async () => {
    setIsCalculating(true);
    setError('');

    // Try API first with a timeout
    const apiPromise = axios.post('/api/calculate', {
      items,
      attendees,
      tipPercentage
    });

    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('API timeout')), 5000)
    );

    try {
      const response = await Promise.race([apiPromise, timeoutPromise]);

      if (response.data.success) {
        onCalculate(response.data.calculations);
        console.log('API calculation successful');
        return;
      } else {
        throw new Error('API returned unsuccessful response');
      }
    } catch (err) {
      console.error('API calculation failed:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        items: items,
        attendees: attendees,
        tipPercentage: tipPercentage
      });
      
      // Always try fallback calculation
      console.log('Attempting fallback calculation...');
      try {
        const fallbackCalculations = calculateBillSplitFallback(items, attendees, tipPercentage);
        onCalculate(fallbackCalculations);
        console.log('Fallback calculation successful');
      } catch (fallbackErr) {
        console.error('Fallback calculation failed:', fallbackErr);
        setError('Failed to calculate bill split. Please check your data and try again.');
      }
    } finally {
      setIsCalculating(false);
    }
  };

  // Fallback calculation function (client-side)
  const calculateBillSplitFallback = (items, attendees, tipPercentage) => {
    console.log('Fallback calculation started with:', { items: items.length, attendees: attendees.length, tipPercentage });
    
    if (!items || !attendees || items.length === 0 || attendees.length === 0) {
      throw new Error('Invalid input: items and attendees are required');
    }

    const results = {};
    
    // Initialize results for each attendee
    attendees.forEach(attendee => {
      if (!attendee || typeof attendee !== 'string') {
        console.warn('Invalid attendee:', attendee);
        return;
      }
      results[attendee] = {
        items: [],
        subtotal: 0,
        tip: 0,
        total: 0
      };
    });

    // Calculate subtotals for each attendee
    items.forEach((item, index) => {
      console.log(`Processing item ${index}:`, item);
      
      if (!item || !item.name || typeof item.price !== 'number') {
        console.warn('Invalid item:', item);
        return;
      }

      if (item.allocations && typeof item.allocations === 'object') {
        Object.entries(item.allocations).forEach(([attendee, quantity]) => {
          if (quantity > 0 && results[attendee]) {
            const itemCost = (item.price * quantity);
            console.log(`  Allocating to ${attendee}: ${quantity} x ${item.price} = ${itemCost}`);
            results[attendee].items.push({
              name: item.name,
              quantity: quantity,
              price: item.price,
              total: itemCost
            });
            results[attendee].subtotal += itemCost;
          }
        });
      } else {
        console.warn(`Item ${item.name} has no valid allocations:`, item.allocations);
      }
    });

    // Calculate tips and totals
    Object.keys(results).forEach(attendee => {
      const subtotal = results[attendee].subtotal;
      const tip = subtotal * ((tipPercentage || 0) / 100);
      const total = subtotal + tip;
      
      // Keep precise values with 2 decimal places
      results[attendee].tip = Math.round(tip * 100) / 100;
      results[attendee].total = Math.round(total * 100) / 100;
      console.log(`${attendee} total: subtotal=${subtotal}, tip=${tip}, total=${total}`);
    });

    console.log('Fallback calculation completed:', results);
    return results;
  };

  const getTotalBill = () => {
    if (!calculations) return 0;
    return Object.values(calculations).reduce((sum, calc) => sum + calc.total, 0);
  };

  const getSubtotal = () => {
    if (!calculations) return 0;
    return Object.values(calculations).reduce((sum, calc) => sum + calc.subtotal, 0);
  };

  const getTotalTip = () => {
    if (!calculations) return 0;
    return Object.values(calculations).reduce((sum, calc) => sum + calc.tip, 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const downloadSummary = () => {
    if (!calculations) return;

    const summary = `
BILL SPLIT SUMMARY
==================

${attendees.map(attendee => {
  const calc = calculations[attendee];
  return `${attendee}:
  Items: ${calc.items.map(item => `${item.name} (${item.quantity}x) - ${formatCurrency(item.total)}`).join('\n    ')}
  Subtotal: ${formatCurrency(calc.subtotal)}
  Tip (${tipPercentage}%): ${formatCurrency(calc.tip)}
  Total: ${formatCurrency(calc.total)}
`;
}).join('\n')}

TOTALS:
Subtotal: ${formatCurrency(getSubtotal())}
Tip: ${formatCurrency(getTotalTip())}
Grand Total: ${formatCurrency(getTotalBill())}
    `;

    const blob = new Blob([summary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bill-split-summary.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Bill Summary</h2>
        <p className="text-gray-600">Review totals and add tip</p>
      </div>

      {error && (
        <div className="max-w-2xl mx-auto p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-red-700">{error}</p>
            <div className="flex space-x-2">
              <button
                onClick={calculateBill}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
              <button
                onClick={() => {
                  console.log('Debug: Testing fallback calculation with current data');
                  console.log('Items:', items);
                  console.log('Attendees:', attendees);
                  console.log('Tip Percentage:', tipPercentage);
                  try {
                    const testResult = calculateBillSplitFallback(items, attendees, tipPercentage);
                    console.log('Test calculation result:', testResult);
                    onCalculate(testResult);
                  } catch (err) {
                    console.error('Test calculation failed:', err);
                    setError('Test calculation failed: ' + err.message);
                  }
                }}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                Debug Test
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tip Settings */}
        <div className="lg:col-span-1">
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tip Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tip Percentage
                </label>
                <input
                  type="number"
                  value={tipPercentage}
                  onChange={(e) => onTipChange(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.1"
                  className="input-field"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[5, 10, 15].map(percentage => (
                  <button
                    key={percentage}
                    onClick={() => onTipChange(percentage)}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      tipPercentage === percentage
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {percentage}%
                  </button>
                ))}
              </div>

              <div className="pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{formatCurrency(getSubtotal())}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tip ({tipPercentage}%):</span>
                  <span className="font-medium">{formatCurrency(getTotalTip())}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total:</span>
                  <span>{formatCurrency(getTotalBill())}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Individual Totals */}
        <div className="lg:col-span-2">
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Individual Totals</h3>
            
            {isCalculating ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-3">
                  <Calculator className="h-6 w-6 text-primary-600 animate-pulse" />
                  <span className="text-gray-600">Calculating...</span>
                </div>
              </div>
            ) : calculations ? (
              <div className="space-y-4">
                {attendees.map(attendee => {
                  const calc = calculations[attendee];
                  return (
                    <div key={attendee} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{attendee}</h4>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary-600">
                            {formatCurrency(calc.total)}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm">
                        {calc.items.map((item, index) => (
                          <div key={index} className="flex justify-between text-gray-600">
                            <span>{item.name} ({item.quantity}x)</span>
                            <span>{formatCurrency(item.total)}</span>
                          </div>
                        ))}
                        
                        <div className="flex justify-between text-gray-600 pt-2 border-t">
                          <span>Subtotal:</span>
                          <span>{formatCurrency(calc.subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Tip ({tipPercentage}%):</span>
                          <span>{formatCurrency(calc.tip)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calculator className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Calculations will appear here</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-6 border-t">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to allocation</span>
        </button>

        <div className="flex items-center space-x-3">
          {calculations && (
            <button
              onClick={downloadSummary}
              className="btn-secondary flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Download Summary</span>
            </button>
          )}
          
          <button
            onClick={onReset}
            className="btn-secondary flex items-center space-x-2"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Start Over</span>
          </button>
        </div>
      </div>

      {calculations && (
        <div className="text-center">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-2xl mx-auto">
            <h3 className="font-medium text-green-900 mb-2">✅ Bill split complete!</h3>
            <p className="text-sm text-green-700">
              Each person's total includes their items plus their share of the tip. 
              You can download the summary or start over with a new bill.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillSummary;
